import { getTriggerOutputVariableItems } from '../subtypes/trigger/triggerOutputVariables';

const safeString = (v) => (v ?? '').toString();

function uniqByToken(items) {
  const seen = new Set();
  const out = [];
  for (const it of (items ?? [])) {
    const token = safeString(it?.token).trim();
    if (!token || seen.has(token)) continue;
    seen.add(token);
    out.push({
      token,
      snippet: safeString(it?.snippet).trim(),
      label: safeString(it?.label).trim(),
    });
  }
  return out;
}

function toEntityTypeNameLookup(metadata) {
  const entityTypes = metadata?.entityTypes ?? metadata?.EntityTypes ?? [];
  const list = Array.isArray(entityTypes) ? entityTypes : [];
  const byKey = new Map();

  for (const entityType of list) {
    const key = safeString(entityType?.Key ?? entityType?.key ?? entityType?.Type ?? entityType?.type).trim();
    const name = safeString(entityType?.Name ?? entityType?.name).trim();
    if (key && name) byKey.set(key.toLowerCase(), name);
  }

  return byKey;
}

function toEntityFieldTemplateItems(metadata) {
  const entities = metadata?.entities ?? metadata?.Entities ?? [];
  const list = Array.isArray(entities) ? entities : [];
  const entityTypeNames = toEntityTypeNameLookup(metadata);

  const entityItems = [];

  for (const e of list) {
    const entityTypeKey = safeString(e?.EntityTypeKey ?? e?.entityTypeKey).trim();
    const runtimeKey = safeString(e?.Key ?? e?.key ?? e?.Name ?? e?.name).trim();
    const entityKey = entityTypeKey || runtimeKey;
    if (!entityKey) continue;
    const entityLabel = entityTypeNames.get(entityKey.toLowerCase()) || entityKey;

    const fields = e?.fields ?? e?.Fields ?? [];
    const fList = Array.isArray(fields) ? fields : [];

    const fieldItems = [];
    for (const f of fList) {
      const apiName = safeString(f?.ApiName ?? f?.apiName ?? f?.Name ?? f?.name).trim();
      const key = safeString(f?.Key ?? f?.key).trim();
      if (!apiName) continue;

      const token = `{{${entityKey}.${apiName}}}`;
      const snippet = `${entityKey}.${apiName}`;
      const label = key ? `${apiName} (${key})` : apiName;
      fieldItems.push({ token, snippet, label });
    }

    fieldItems.sort((a, b) => a.token.localeCompare(b.token));
    if (fieldItems.length > 0) {
      entityItems.push({ label: entityLabel, items: fieldItems });
    }
  }

  entityItems.sort((a, b) => safeString(a?.label).localeCompare(safeString(b?.label)));
  return entityItems;
}

const normalizeTypeLikeName = (value) => {
  let name = safeString(value).trim();
  if (!name) return '';
  name = name.replace(/^v\d+_/i, '');
  name = name.replace(/RequestModel$/i, '');
  name = name.replace(/^IEnumerable<(.+)>$/i, '$1');
  name = name.replace(/^List<(.+)>$/i, '$1');
  return name.trim();
};

const isCollectionLikeType = (typeName) => {
  const t = safeString(typeName).trim();
  if (!t) return false;
  return /IEnumerable<|List<|Collection<|\[\]$/i.test(t);
};

const toEntityLookup = (metadata) => {
  const entities = metadata?.entities ?? metadata?.Entities ?? [];
  const list = Array.isArray(entities) ? entities : [];
  const byKey = new Map();
  for (const e of list) {
    const entityTypeKey = safeString(e?.EntityTypeKey ?? e?.entityTypeKey).trim();
    const key = safeString(e?.Key ?? e?.key).trim();
    const apiType = safeString(e?.ApiType ?? e?.apiType).trim();
    const normalizedApiType = normalizeTypeLikeName(apiType).toLowerCase();
    if (entityTypeKey) byKey.set(entityTypeKey.toLowerCase(), e);
    if (key) byKey.set(key.toLowerCase(), e);
    if (normalizedApiType) byKey.set(normalizedApiType, e);
  }
  return byKey;
};

const resolveChildEntity = (field, byKey) => {
  if (!field || !byKey) return null;
  const apiName = safeString(field?.ApiName ?? field?.apiName ?? field?.Name ?? field?.name).trim();
  const runtimeKey = safeString(field?.Key ?? field?.key).trim();
  const typeName = normalizeTypeLikeName(field?.Type ?? field?.type);
  const candidates = [apiName, runtimeKey, typeName]
    .map(x => safeString(x).trim().toLowerCase())
    .filter(Boolean);
  for (const c of candidates) {
    const match = byKey.get(c);
    if (match) return match;
  }
  return null;
};

const buildAliasFieldItems = ({
  entity,
  aliasKey,
  byKey,
  depth = 0,
  maxDepth = 4,
  pathPrefix = '',
  visitedEntityKeys = new Set(),
}) => {
  const fields = entity?.fields ?? entity?.Fields ?? [];
  const fieldList = Array.isArray(fields) ? fields : [];
  const nextVisited = new Set(visitedEntityKeys);
  const entityKey = safeString(entity?.EntityTypeKey ?? entity?.entityTypeKey ?? entity?.Key ?? entity?.key).trim().toLowerCase();
  if (entityKey) nextVisited.add(entityKey);

  const items = fieldList
    .map((field) => {
      const apiName = safeString(field?.ApiName ?? field?.apiName ?? field?.Name ?? field?.name).trim();
      const key = safeString(field?.Key ?? field?.key).trim();
      if (!apiName) return null;

      const apiPath = pathPrefix ? `${pathPrefix}.${apiName}` : apiName;
      const token = `{{${aliasKey}.${apiPath}}}`;
      const snippet = `${aliasKey}.${apiPath}`;
      const label = key ? `${apiName} (${key})` : apiName;
      const childEntity = resolveChildEntity(field, byKey);
      const childKey = safeString(childEntity?.EntityTypeKey ?? childEntity?.entityTypeKey ?? childEntity?.Key ?? childEntity?.key).trim().toLowerCase();
      const canExpand = !!childEntity
        && !isCollectionLikeType(field?.Type ?? field?.type)
        && depth < maxDepth
        && !nextVisited.has(childKey);

      if (canExpand) {
        const children = buildAliasFieldItems({
          entity: childEntity,
          aliasKey,
          byKey,
          depth: depth + 1,
          maxDepth,
          pathPrefix: apiPath,
          visitedEntityKeys: nextVisited,
        });
        if (children.length > 0) {
          return {
            label: `${label}...`,
            items: children,
          };
        }
      }

      return { token, snippet, label };
    })
    .filter(Boolean)
    .sort((a, b) => safeString(a?.label ?? a?.token).localeCompare(safeString(b?.label ?? b?.token)));

  return items;
};

function toAliasFieldTemplateGroup(metadata, entityType, aliasKey, groupName) {
  const safeEntityType = safeString(entityType).trim();
  const safeAliasKey = safeString(aliasKey).trim();
  if (!safeEntityType || !safeAliasKey) return null;

  const byKey = toEntityLookup(metadata);
  const match = byKey.get(safeEntityType.toLowerCase()) ?? null;
  if (!match) return null;

  const items = buildAliasFieldItems({
    entity: match,
    aliasKey: safeAliasKey,
    byKey,
  });

  if (items.length === 0) return null;
  return {
    group: safeString(groupName) || safeAliasKey,
    items: [
      {
        label: safeEntityType,
        items,
      },
    ],
  };
}

export function buildAliasFieldTemplateGroup(metadata, entityType, aliasKey, groupName) {
  return toAliasFieldTemplateGroup(metadata, entityType, aliasKey, groupName);
}

// Recursively converts a backend template subgroup ({ Group, Groups, Items }) into the
// nested { label, items } shape that the renderer (and walkTemplateItems in the JS
// assistant dialog) already understands for entity-field templates.
// Returns null when the subtree is empty.
function toMetadataTemplateSubgroup(g) {
  const groupName = safeString(g?.group ?? g?.Group).trim();
  if (!groupName) return null;

  const rawSubGroups = g?.groups ?? g?.Groups ?? [];
  const subGroupNodes = (Array.isArray(rawSubGroups) ? rawSubGroups : [])
    .map(toMetadataTemplateSubgroup)
    .filter(Boolean);

  const rawItems = g?.items ?? g?.Items ?? [];
  const leaves = uniqByToken(
    (Array.isArray(rawItems) ? rawItems : []).map(it => ({
      token: safeString(it?.token ?? it?.Token).trim(),
      snippet: safeString(it?.snippet ?? it?.Snippet).trim(),
      label: safeString(it?.label ?? it?.Label).trim(),
    }))
  );
  leaves.sort((a, b) => a.token.localeCompare(b.token));

  // Subgroups become labelled item-trees; leaf items follow underneath. This matches the
  // shape produced by toEntityFieldTemplateItems / buildAliasFieldItems so the catalog
  // renderer treats nested template groups identically to entity-field nesting.
  const items = [...subGroupNodes, ...leaves];
  if (items.length === 0) return null;

  return { label: groupName, items };
}

function toMetadataTemplateGroups(metadata) {
  const rawTemplates = metadata?.templates ?? metadata?.Templates ?? [];
  const templateGroups = Array.isArray(rawTemplates) ? rawTemplates : [];
  const groups = [];

  for (const g of templateGroups) {
    const groupName = safeString(g?.group ?? g?.Group).trim() || 'Templates';
    const subgroup = toMetadataTemplateSubgroup(g);
    if (!subgroup) continue;
    // The outer entry uses { group, items } (top-level catalog shape); inner nodes
    // use { label, items } (sub-tree shape). Hoist the subgroup's items here so the
    // top-level group exposes both nested branches and leaf items.
    groups.push({ group: groupName, items: subgroup.items });
  }

  return groups;
}

function toCustomSettingTemplateGroup(graph, consumerMode) {
  const settings = Array.isArray(graph?.customSettings) ? graph.customSettings : [];
  const items = settings
    .map((setting) => {
      const key = safeString(setting?.key ?? setting?.Key).trim();
      if (!key) return null;
      const label = safeString(setting?.label ?? setting?.Label).trim() || key;
      return formatCatalogItemByConsumer({
        token: `{{settings.${key}}}`,
        snippet: `settings.${key}`,
        label: `${label} (settings.${key})`,
      }, consumerMode);
    })
    .filter(Boolean);

  if (items.length === 0) return null;
  return { group: 'Settings Custom', items: uniqByToken(items) };
}

export function buildWorkflowMetadataTemplateCatalog(metadata) {
  return toMetadataTemplateGroups(metadata);
}

// Output variable items come exclusively from the server's output reference schemas
// (workflow metadata); there is no client-side fallback catalog.
function toDescriptorVariableItems(config, ctx) {
  return toSchemaVariableItems(ctx?.metadata, ctx?.kind, ctx?.subtype, config, ctx?.consumerMode);
}

function toOutputReferenceSchemaLookup(metadata) {
  const raw = metadata?.outputReferenceSchemas ?? metadata?.OutputReferenceSchemas ?? [];
  const list = Array.isArray(raw) ? raw : [];
  const map = new Map();
  for (const schema of list) {
    const kind = safeString(schema?.kind ?? schema?.Kind).trim().toLowerCase();
    const subtype = safeString(schema?.subtype ?? schema?.Subtype).trim().toLowerCase();
    if (!kind || !subtype) continue;
    map.set(`${kind}:${subtype}`, schema);
  }
  return map;
}

function isTriggerLocalScope(scope) {
  const normalized = safeString(scope).trim().toLowerCase();
  return normalized === 'triggerlocalonly' || normalized === 'triggereditoronly';
}

function isScopeAllowedForCatalog(scope, accessContext) {
  if (!scope) return true;
  if (isTriggerLocalScope(scope)) {
    return accessContext === 'triggerLocal';
  }
  return true;
}

function pathTemplateMatches(schemaPathTemplate, requestedPathTemplate) {
  const schemaPath = safeString(schemaPathTemplate).trim();
  const requestedPath = safeString(requestedPathTemplate).trim();
  if (!schemaPath || !requestedPath) return false;

  if (schemaPath.toLowerCase() === requestedPath.toLowerCase()) {
    return true;
  }

  if (schemaPath.toLowerCase().endsWith('.*')) {
    const prefix = schemaPath.slice(0, -2);
    return requestedPath.toLowerCase().startsWith(`${prefix.toLowerCase()}.`)
      || schemaPath.toLowerCase() === requestedPath.toLowerCase();
  }

  return false;
}

function hasAllowedSchemaReference(metadata, kind, subtype, referenceKind, pathTemplate, accessContext, consumerMode) {
  const lookup = toOutputReferenceSchemaLookup(metadata);
  const schema = lookup.get(`${safeString(kind).trim().toLowerCase()}:${safeString(subtype).trim().toLowerCase()}`);
  const refs = Array.isArray(schema?.references ?? schema?.References) ? (schema?.references ?? schema?.References) : [];
  const mode = (consumerMode ?? 'template').toString().trim().toLowerCase() || 'template';

  return refs.some((ref) => {
    const consumers = Array.isArray(ref?.consumers ?? ref?.Consumers) ? (ref?.consumers ?? ref?.Consumers) : [];
    const normalizedConsumers = consumers.map(x => safeString(x).trim().toLowerCase()).filter(Boolean);
    if (normalizedConsumers.length > 0 && !normalizedConsumers.includes(mode)) return false;
    if (!isScopeAllowedForCatalog(ref?.scope ?? ref?.Scope, accessContext)) return false;
    if (safeString(ref?.referenceKind ?? ref?.ReferenceKind).trim().toLowerCase() !== safeString(referenceKind).trim().toLowerCase()) return false;
    return pathTemplateMatches(ref?.pathTemplate ?? ref?.PathTemplate, pathTemplate);
  });
}

function resolveReferenceTemplate(template, config) {
  const raw = safeString(template).trim();
  if (!raw) return '';
  return raw.replace(/\{([A-Za-z0-9_]+)(?::([^}]*))?\}/g, (_, key, defaultValue) => {
    const configured = safeString(config?.[key]).trim();
    return configured || safeString(defaultValue);
  });
}

function formatWorkflowVariableItem(rawKey, consumerMode) {
  const raw = safeString(rawKey).trim();
  if (!raw) return null;
  const inner = raw.replace(/^\{\{\s*|\s*\}\}$/g, '').trim();
  const variablePath = inner.startsWith('vars.') ? inner.slice(5) : inner;
  if (!variablePath) return null;

  if ((consumerMode ?? 'template') === 'javascript') {
    const jsRef = `ctx.vars.${variablePath}`;
    return { token: jsRef, snippet: jsRef, label: jsRef };
  }

  const token = `{{vars.${variablePath}}}`;
  return { token, snippet: `vars.${variablePath}`, label: token };
}

function formatTemplateTokenItem(rawKey, consumerMode) {
  const raw = safeString(rawKey).trim();
  if (!raw) return null;
  const inner = raw.replace(/^\{\{\s*|\s*\}\}$/g, '').trim();
  if (!inner) return null;

  if ((consumerMode ?? 'template') === 'javascript') {
    if (inner.startsWith('vars.')) {
      const jsRef = `ctx.${inner}`;
      return { token: jsRef, snippet: jsRef, label: jsRef };
    }
    if (inner.startsWith('settings.')) {
      const jsRef = `ctx.${inner}`;
      return { token: jsRef, snippet: jsRef, label: jsRef };
    }
    if (inner.startsWith('trigger.') || inner.startsWith('triggerEntity.') || inner.startsWith('workflowEntity.') || inner.startsWith('workflow.') || inner.startsWith('tenant.') || inner.startsWith('run.') || inner.startsWith('wait.') || inner.startsWith('auditEntry.') || inner.startsWith('err.')) {
      const jsRef = `ctx.${inner}`;
      return { token: jsRef, snippet: jsRef, label: jsRef };
    }
    const jsRef = `ctx.${inner}`;
    return { token: jsRef, snippet: jsRef, label: jsRef };
  }

  const token = `{{${inner}}}`;
  return { token, snippet: inner, label: token };
}

function formatCatalogItemByConsumer(item, consumerMode) {
  const token = safeString(item?.token).trim();
  const snippet = safeString(item?.snippet).trim();
  const label = safeString(item?.label).trim() || token || snippet;
  const mode = (consumerMode ?? 'template').toString().trim().toLowerCase() || 'template';
  if (!token && !snippet) return null;

  if (mode === 'javascript') {
    let jsValue = snippet || token.replace(/^\{\{\s*|\s*\}\}$/g, '');
    if (/^(vars|settings|trigger|triggerEntity|workflowEntity|workflow|tenant|run|wait|auditEntry|err)\./.test(jsValue)) {
      jsValue = `ctx.${jsValue}`;
    }
    if (!jsValue) return null;
    return { token: jsValue, snippet: jsValue, label: jsValue };
  }

  const templateToken = token || snippet;
  return { token: templateToken, snippet: snippet || templateToken, label: label || templateToken };
}

function toSchemaVariableItems(metadata, kind, subtype, config, consumerMode, accessContext = 'downstream') {
  const lookup = toOutputReferenceSchemaLookup(metadata);
  const schema = lookup.get(`${safeString(kind).trim().toLowerCase()}:${safeString(subtype).trim().toLowerCase()}`);
  const refs = Array.isArray(schema?.references ?? schema?.References) ? (schema?.references ?? schema?.References) : [];
  const mode = (consumerMode ?? 'template').toString().trim().toLowerCase() || 'template';
  const items = [];

  for (const ref of refs) {
    const consumers = Array.isArray(ref?.consumers ?? ref?.Consumers) ? (ref?.consumers ?? ref?.Consumers) : [];
    const normalizedConsumers = consumers.map(x => safeString(x).trim().toLowerCase()).filter(Boolean);
    if (normalizedConsumers.length > 0 && !normalizedConsumers.includes(mode)) continue;
    if (!isScopeAllowedForCatalog(ref?.scope ?? ref?.Scope, accessContext)) continue;

    const referenceKind = safeString(ref?.referenceKind ?? ref?.ReferenceKind).trim().toLowerCase();
    const pathTemplate = safeString(ref?.pathTemplate ?? ref?.PathTemplate).trim();
    const resolvedPath = resolveReferenceTemplate(pathTemplate, config);
    if (!resolvedPath) continue;

    if (referenceKind === 'workflowvariable') {
      const formatted = formatWorkflowVariableItem(resolvedPath, mode);
      if (formatted) items.push(formatted);
      continue;
    }

    if (referenceKind === 'templatetoken') {
      const formatted = formatTemplateTokenItem(resolvedPath, mode);
      if (formatted) items.push(formatted);
    }
  }

  return items;
}

function toSchemaVariableRows(metadata, kind, subtype, config, consumerMode, accessContext = 'downstream') {
  const lookup = toOutputReferenceSchemaLookup(metadata);
  const schema = lookup.get(`${safeString(kind).trim().toLowerCase()}:${safeString(subtype).trim().toLowerCase()}`);
  const refs = Array.isArray(schema?.references ?? schema?.References) ? (schema?.references ?? schema?.References) : [];
  const mode = (consumerMode ?? 'template').toString().trim().toLowerCase() || 'template';
  const rows = [];

  for (const ref of refs) {
    const consumers = Array.isArray(ref?.consumers ?? ref?.Consumers) ? (ref?.consumers ?? ref?.Consumers) : [];
    const normalizedConsumers = consumers.map(x => safeString(x).trim().toLowerCase()).filter(Boolean);
    if (normalizedConsumers.length > 0 && !normalizedConsumers.includes(mode)) continue;
    if (!isScopeAllowedForCatalog(ref?.scope ?? ref?.Scope, accessContext)) continue;

    const referenceKind = safeString(ref?.referenceKind ?? ref?.ReferenceKind).trim().toLowerCase();
    const resolvedPath = resolveReferenceTemplate(ref?.pathTemplate ?? ref?.PathTemplate, config);
    const availability = safeString(ref?.availability ?? ref?.Availability).trim();
    if (referenceKind !== 'workflowvariable' || !resolvedPath) continue;

    const formatted = formatWorkflowVariableItem(resolvedPath, mode);
    if (!formatted?.token) continue;
    rows.push({
      key: formatted.token,
      availability,
      description: safeString(ref?.description ?? ref?.Description).trim(),
      example: safeString(ref?.example ?? ref?.Example).trim(),
      simulationOnly: !!(ref?.simulationOnly ?? ref?.SimulationOnly),
    });
  }

  return rows;
}

function getJavascriptHelperItems() {
  return [
    { token: 'toDate(value)', snippet: 'toDate(vars.someDate)', label: 'toDate(value)' },
    { token: 'toIso(value)', snippet: 'toIso(vars.someDate)', label: 'toIso(value)' },
    { token: 'addMinutes(date, amount)', snippet: 'addMinutes(vars.someDate, 30)', label: 'addMinutes(date, amount)' },
    { token: 'addHours(date, amount)', snippet: 'addHours(vars.someDate, 3)', label: 'addHours(date, amount)' },
    { token: 'addDays(date, amount)', snippet: 'addDays(vars.someDate, 3)', label: 'addDays(date, amount)' },
    { token: 'addWeeks(date, amount)', snippet: 'addWeeks(vars.someDate, 1)', label: 'addWeeks(date, amount)' },
    { token: 'addMonths(date, amount)', snippet: 'addMonths(vars.someDate, 1)', label: 'addMonths(date, amount)' },
  ];
}

/**
 * Build the list of available Handlebars templates for a workflow node.
 *
 * Notes:
 * - Trigger templates come from the trigger descriptor's `listOutputs(config, ctx)`.
 * - Output variable templates come from the server's output reference schemas in metadata.
 * - Entity field templates come from workflow metadata (all entities and their fields).
 * - Built-in/global template groups come from workflow metadata Templates (e.g. Settings).
 *
 * Output:
 * [
 *   { group: 'Entities', items: [{ label: 'Job', items: [{token,label}, ...] }, ...] },
 *   { group: 'Trigger', items: [{ token, label }] },
 *   { group: 'Variables', items: [{ token, label }] },
 *   { group: 'WaitFor', items: [{ token, label }] },
 * ]
 */
export function buildWorkflowTemplateCatalog({
  graph,
  currentNodeId,
  metadata,
  workflowEntityType,
  includeCurrentNodeOutputs = false,
  includeTriggerItems = true,
  includeTriggerEntityGroups = true,
  consumerMode = 'template',
  getDescriptor: descriptorResolver,
} = {}) {
  const nodes = Array.isArray(graph?.nodes) ? graph.nodes : [];
  const edges = Array.isArray(graph?.edges) ? graph.edges : [];

  const nodesById = new Map(nodes.map(n => [n?.id, n]));
  const startId = safeString(currentNodeId).trim();
  const hasStart = !!startId;

  // BFS upstream traversal via incoming edges.
  let considerNodes = nodes;
  if (hasStart) {
    const upstreamIds = new Set();
    const queue = [startId];
    const visited = new Set([startId]);
    while (queue.length > 0) {
      const id = queue.shift();
      const incoming = edges.filter(e => safeString(e?.target) === id);
      for (const e of incoming) {
        const srcId = safeString(e?.source);
        if (!srcId || visited.has(srcId)) continue;
        visited.add(srcId);
        upstreamIds.add(srcId);
        queue.push(srcId);
      }
    }

    const considerIds = includeCurrentNodeOutputs ? new Set([...upstreamIds, startId]) : upstreamIds;
    considerNodes = [...considerIds].map(id => nodesById.get(id)).filter(Boolean);
  }

  const triggerItems = [];
  const variableItems = [];
  const waitForItems = [];
  const metadataTemplateGroups = toMetadataTemplateGroups(metadata);
  const customSettingGroup = toCustomSettingTemplateGroup(graph, consumerMode);
  const triggerEntityTypes = new Set();
  // Downstream nodes always see the workflow entity; a TRIGGER node only sees it when
  // its schema declares workflowEntity.* (timer triggers run before any workflow entity
  // exists, so offering the group there produces tokens that resolve to blank).
  let allowWorkflowEntityGroup = true;

  for (const n of considerNodes) {
    const kind = safeString(n?.data?.kind);
    const subtype = safeString(n?.data?.subtype);
    const config = (n?.data?.config && typeof n.data.config === 'object') ? n.data.config : {};
    const descriptor = typeof descriptorResolver === 'function' ? descriptorResolver(kind, subtype) : null;
    const accessContext = (kind === 'trigger' && safeString(n?.id) === startId)
      ? 'triggerLocal'
      : 'downstream';
    const descriptorCtx = { kind, subtype, metadata, consumerMode, accessContext };

    if (kind === 'trigger') {
      const triggerEntityType = safeString(config?.entityTypeRuntime ?? config?.entityType).trim();
      const allowTriggerEntityGroup = !!triggerEntityType && hasAllowedSchemaReference(
        metadata,
        kind,
        subtype,
        'entityAliasField',
        'triggerEntity.*',
        accessContext,
        consumerMode
      );
      if (allowTriggerEntityGroup) {
        triggerEntityTypes.add(triggerEntityType);
      }
      if (accessContext === 'triggerLocal') {
        allowWorkflowEntityGroup = hasAllowedSchemaReference(
          metadata,
          kind,
          subtype,
          'entityAliasField',
          'workflowEntity.*',
          accessContext,
          consumerMode
        );
      }
      const schemaTriggerItems = toSchemaVariableItems(metadata, kind, subtype, config, consumerMode, accessContext);
      const list = schemaTriggerItems.length > 0
        ? schemaTriggerItems
        : (descriptor?.listOutputs?.(config, descriptorCtx) ?? []);
      const arr = Array.isArray(list) ? list : [];
      for (const o of arr) {
        const label = typeof o?.label === 'function' ? safeString(o.label()) : safeString(o?.label);
        const formatted = formatCatalogItemByConsumer({
          token: safeString(o?.token).trim(),
          snippet: safeString(o?.snippet).trim() || safeString(o?.token).trim().replace(/^\{\{\s*|\s*\}\}$/g, ''),
          label,
        }, consumerMode);
        if (formatted) {
          triggerItems.push(formatted);
        }
      }

      const triggerVariableItems = getTriggerOutputVariableItems(config);
      if (triggerVariableItems.length > 0) {
        variableItems.push(
          ...triggerVariableItems
            .map((item) => formatCatalogItemByConsumer(item, consumerMode))
            .filter(Boolean)
        );
      }
    }

    const descriptorItems = toDescriptorVariableItems(config, descriptorCtx);
    if (descriptorItems.length > 0) {
      if (kind === 'waitFor') {
        waitForItems.push(...descriptorItems);
      } else if (kind !== 'trigger') {
        variableItems.push(...descriptorItems);
      }
    }
  }

  const workflowEntityGroup = allowWorkflowEntityGroup
    ? toAliasFieldTemplateGroup(metadata, workflowEntityType, 'workflowEntity', 'Workflow Entity')
    : null;
  const triggerEntityGroups = includeTriggerEntityGroups
    ? [...triggerEntityTypes]
      .sort((a, b) => a.localeCompare(b))
      .map((entityType) => toAliasFieldTemplateGroup(metadata, entityType, 'triggerEntity', 'Trigger Entity'))
      .filter(Boolean)
    : [];

  const groups = [
    ...metadataTemplateGroups,
    ...(customSettingGroup ? [customSettingGroup] : []),
    ...(workflowEntityGroup ? [workflowEntityGroup] : []),
    ...triggerEntityGroups,
    { group: 'JavaScript Helpers', items: uniqByToken(getJavascriptHelperItems()) },
    ...(includeTriggerItems ? [{ group: 'Trigger', items: uniqByToken(triggerItems) }] : []),
    { group: 'Variables', items: uniqByToken(variableItems) },
    { group: 'WaitFor', items: uniqByToken(waitForItems) },
  ].filter(g => (g.items?.length ?? 0) > 0);

  for (const g of groups) {
    // Items may be a mix of leaf entries ({ token, ... }) and labelled subtrees
    // ({ label, items: [...] }, e.g. nested template groups or entity-field trees).
    // Sort by whichever key is present so neither branch crashes the comparator.
    g.items.sort((a, b) => {
      const aKey = safeString(a?.token ?? a?.label).trim();
      const bKey = safeString(b?.token ?? b?.label).trim();
      return aKey.localeCompare(bKey);
    });
  }

  return groups;
}

export function buildWorkflowEntityFieldCatalog(metadata) {
  const items = toEntityFieldTemplateItems(metadata);
  if ((items?.length ?? 0) === 0) return null;
  return { group: 'Entities', items };
}

// Output variable rows come exclusively from the server's output reference schemas
// (workflow metadata); there is no client-side fallback catalog. Callers must wait
// for metadata to load before rendering these rows.
export function buildWorkflowOutputVariableRows({
  metadata,
  kind,
  subtype,
  config,
  consumerMode = 'template',
  accessContext = 'downstream',
} = {}) {
  return toSchemaVariableRows(metadata, kind, subtype, config, consumerMode, accessContext);
}

